/** The lines between two counter rows of an officer's schedule text: their duties by name, the rest as BREAK. */

import { hmmmToEndSlot } from '@/config/modeConfig';
import { exportSlotToTime } from '@/components/tabs/home/gridData';
import { minutesBetween } from '@/components/OfficerCard';

/**
 * `from` is where the previous row ENDS, so it is an exclusive end (hmmmToEndSlot), not a slot to start from:
 * a row ending at the end of the shift ("2200") must give slot 48 — nothing left — not the last slot (2145),
 * which printed a break the officer never takes. `to` null is the end of the shift, where only a duty is
 * worth a line.
 */
export function scheduleGapLines(
  from: string,
  to: string | null,
  startHour: number,
  duties: Record<string, string> | undefined,
): string[] {
  const breakLine = (a: string, b: string) => {
    const gap = minutesBetween(a, b);
    if (gap <= 0) return null;
    const h = Math.floor(gap / 60); const m = gap % 60;
    const dur = h > 0 && m > 0 ? `(${h}h ${m}min)` : h > 0 ? `(${h}h)` : `(${m}min)`;
    return `${a}-${b}: BREAK ${dur}`;
  };
  let a: number, b: number;
  try {
    a = hmmmToEndSlot(from, startHour);
    b = to === null ? 48 : hmmmToEndSlot(to, startHour);
  } catch {
    const only = to === null ? null : breakLine(from, to);
    return only ? [only] : [];
  }
  const out: string[] = [];
  let k = a;
  while (k < b) {
    const duty = duties?.[String(k)] ?? '';
    let j = k;
    while (j < b && (duties?.[String(j)] ?? '') === duty) j++;
    const start = exportSlotToTime(k, startHour);
    const end = exportSlotToTime(j, startHour);
    if (duty) out.push(`${start}-${end}: ${duty}`);
    else if (to !== null) { const line = breakLine(start, end); if (line) out.push(line); }
    k = j;
  }
  return out;
}

/** The note a counter line gets when its counter goes unmanned as the officer leaves it. */
export const NO_RELIEF_NOTE = '(no relief)';

/**
 * One counter line of an officer's schedule text — "1000-1145: DC31", with " (no relief)" when nobody takes
 * the counter over at `end` (`noReliefEnds` holds `${counter0}:${slot}` keys, see utils/noRelief).
 */
export function scheduleCounterLine(
  row: { startTime: string; endTime: string; counter: string },
  label: string,
  startHour: number,
  noReliefEnds: Set<string>,
): string {
  let noRelief = false;
  try {
    noRelief = noReliefEnds.has(`${parseInt(row.counter, 10) - 1}:${hmmmToEndSlot(row.endTime, startHour)}`);
  } catch { /* unparsable end: no note */ }
  return `${row.startTime}-${row.endTime}: ${label}${noRelief ? ` ${NO_RELIEF_NOTE}` : ''}`;
}
